/**
 * Sync engine — drains the offline queue when connectivity returns.
 *
 * Each queued operation is retried once per drain cycle. On success the
 * op is removed; on failure the retry counter is bumped and the op stays
 * queued (so it will be retried on the next reconnect / next drain).
 *
 * All backend endpoints accept ``operation_id`` for idempotent replay, so
 * a network failure between "server processed" and "client removed from
 * queue" is safe — retrying will hit the fast-path replay branch.
 */
import axios from 'axios';
import { getQueue, removeOperation, bumpRetry, getDocBlob, removeDocBlob } from '../db/offlineStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _handleLoadStatus = async (op) => {
  await axios.post(
    `${API}/loads/${op.loadId}/status`,
    { ...op.payload, operation_id: op.opId },
    { withCredentials: true }
  );
};

const _handleLoadDocument = async (op) => {
  // Re-hydrate the blob from IDB and re-post as multipart FormData with the
  // same operation_id so the server can de-dupe replays.
  const stored = await getDocBlob(op.opId);
  if (!stored || !stored.blob) {
    // Nothing to upload — drop the op silently so we don't loop forever.
    return;
  }
  const fd = new FormData();
  fd.append('file', stored.blob, stored.fileName);
  fd.append('doc_kind', stored.docKind);
  if (stored.notes) fd.append('notes', stored.notes);
  fd.append('operation_id', op.opId);
  await axios.post(`${API}/loads/${op.loadId}/documents`, fd, { withCredentials: true });
  // Purge the blob only after the server has confirmed the upload.
  await removeDocBlob(op.opId);
};

const _handleDriverRequest = async (op) => {
  await axios.post(
    `${API}/driver-requests`,
    { ...op.payload, operation_id: op.opId },
    { withCredentials: true }
  );
};

const HANDLERS = {
  load_status: _handleLoadStatus,
  load_document: _handleLoadDocument,
  driver_request: _handleDriverRequest,
};

/** Drain every queued op. Returns { ok, failed, conflicts } counts.
 *
 * ``conflicts`` is a list of load ids whose server-side ``updated_at`` is
 * newer than the ``updated_at`` we last saw on the cached copy — i.e.
 * dispatch changed appointments / driver / rate while the phone was
 * offline. We flag these so the UI can surface a "some loads changed
 * while you were offline" notice; no automatic resolution is performed.
 */
export const drainQueue = async () => {
  const queue = await getQueue();
  let ok = 0;
  let failed = 0;
  for (const op of queue) {
    const handler = HANDLERS[op.kind];
    if (!handler) {
      await removeOperation(op.opId);
      continue;
    }
    try {
      await handler(op);
      await removeOperation(op.opId);
      ok += 1;
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'unknown';
      await bumpRetry(op.opId, typeof msg === 'string' ? msg : JSON.stringify(msg));
      failed += 1;
    }
  }

  // Conflict detection — compare each cached load's ``updated_at`` against
  // the server's current value. Only runs if we actually have any cached
  // loads; skips silently if the /loads fetch fails (still offline / auth
  // expired / etc.).
  const conflicts = [];
  try {
    const { getCachedLoads, cacheLoads } = await import('../db/offlineStore');
    const cached = await getCachedLoads();
    if (cached && cached.length > 0) {
      const fresh = await axios.get(`${API}/loads`, { withCredentials: true });
      const freshById = new Map((fresh.data || []).map((l) => [l.id, l]));
      for (const c of cached) {
        const s = freshById.get(c.id);
        if (!s) continue;
        if (s.updated_at && c.updated_at && s.updated_at > c.updated_at) {
          conflicts.push({
            load_id: c.id,
            load_ref: s.load_ref || c.load_ref,
            client_status: c.status,
            server_status: s.status,
            // Field-level diff for the manual conflict-resolution UI. We
            // only surface fields drivers actually care about; noise from
            // internal admin/audit fields is deliberately excluded.
            changes: _diffLoadFields(c, s),
            client_updated_at: c.updated_at,
            server_updated_at: s.updated_at,
          });
        }
      }
      // Refresh the local snapshot so we don't re-flag the same conflict
      // on the next drain.
      await cacheLoads(fresh.data || []);
    }
  } catch {
    /* offline or auth expired — non-fatal, next drain will re-check */
  }

  return { ok, failed, total: queue.length, conflicts };
};

// Fields on a load object that a driver cares about when comparing their
// last-cached copy against a fresh server copy. Kept intentionally small
// so the diff modal stays scannable; internal audit / analytics fields
// are excluded on purpose.
const DIFF_FIELDS = [
  { key: 'status', label: 'Status' },
  { key: 'driver_id', label: 'Assigned driver' },
  { key: 'pickup_datetime', label: 'Pickup appointment' },
  { key: 'delivery_datetime', label: 'Delivery appointment' },
  { key: 'pickup_address', label: 'Pickup address' },
  { key: 'delivery_address', label: 'Delivery address' },
  { key: 'rate_usd', label: 'Rate (USD)' },
  { key: 'notes', label: 'Dispatch notes' },
];

const _normalize = (v) => {
  if (v === null || v === undefined || v === '') return null;
  return v;
};

const _diffLoadFields = (client, server) => {
  const changes = [];
  for (const { key, label } of DIFF_FIELDS) {
    const a = _normalize(client?.[key]);
    const b = _normalize(server?.[key]);
    if (a === b) continue;
    // Deep-ish equality for primitive fields is sufficient — every field in
    // DIFF_FIELDS is either a string, number, or null on the wire.
    if (typeof a === 'string' && typeof b === 'string' && a === b) continue;
    changes.push({ key, label, client: a, server: b });
  }
  // Stop count changes are useful even if the array is otherwise unchanged.
  const clientStopCount = Array.isArray(client?.stops) ? client.stops.length : 0;
  const serverStopCount = Array.isArray(server?.stops) ? server.stops.length : 0;
  if (clientStopCount !== serverStopCount) {
    changes.push({
      key: 'stops',
      label: 'Number of stops',
      client: clientStopCount,
      server: serverStopCount,
    });
  }
  return changes;
};
